import { Link, useParams, Navigate } from "react-router-dom";
import { Hexagon, Clock, CalendarDays, Zap, ChevronRight, Check } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import AffiliateCTA from "@/components/AffiliateCTA";
import { Reveal } from "@/components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { trackAffiliateClick } from "@/lib/analytics";
import { registerClick } from "@/lib/clicks";
import { analytics } from "@heycatch/sdk";
import { POSTS } from "@/data/posts";

const renderText = (text) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    const [, label, href] = m;
    const internal = href.startsWith("/");
    return (
      <a
        key={i}
        href={href}
        {...(internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
        className="font-semibold text-amber-700 underline underline-offset-2 decoration-amber-300 hover:text-amber-800 transition-colors"
      >
        {label}
      </a>
    );
  });
};

const Block = ({ block }) => {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-12 font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-900">
          {block.text}
        </h2>
      );
    case "h3":
      return (
        <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-zinc-900">
          {block.text}
        </h3>
      );
    case "list":
      return (
        <ul className="mt-5 space-y-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-base sm:text-lg text-zinc-600 leading-relaxed">
              <span className="mt-1.5 grid place-items-center w-5 h-5 rounded-md bg-amber-400 text-zinc-900 shrink-0">
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
              {item}
            </li>
          ))}
        </ul>
      );
    case "p":
      return <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">{renderText(block.text)}</p>;
    case "callout":
      return (
        <aside className="mt-7 rounded-2xl border-l-4 border-amber-400 bg-amber-50 px-6 py-5">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-700">{block.label}</p>
          <p className="mt-2 text-base font-medium text-zinc-800 leading-relaxed">{block.text}</p>
        </aside>
      );
    case "cta":
      return (
        <div className="my-9 rounded-3xl bg-gradient-to-br from-amber-400 to-amber-500 px-8 py-8 text-center">
          <p className="font-display text-xl font-extrabold text-zinc-900">
            Ready to earn your first daily dollar?
          </p>
          <div className="mt-5">
            <AffiliateCTA label={block.label} testid={block.testid} dark />
          </div>
        </div>
      );
    case "cta-link":
      return (
        <div className="my-9 rounded-3xl bg-zinc-900 px-8 py-8 text-center">
          <p className="font-display text-xl font-extrabold text-white">{block.title}</p>
          {block.text && <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">{block.text}</p>}
          <a
            href={block.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={block.testid}
            onClick={() => {
              trackAffiliateClick(block.testid);
              analytics.trackEvent("partner_offer_click", { offer: "trafficadbar", location: block.testid });
              registerClick("trafficadbar");
            }}
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-400 px-8 py-4 font-display text-base font-bold text-zinc-900 hover:bg-amber-300 transition-colors"
          >
            {block.label}
          </a>
        </div>
      );
    case "img":
      return (
        <figure className="mt-8">
          <img
            src={block.src}
            alt={block.alt}
            loading="lazy"
            className="w-full rounded-3xl border border-zinc-100 shadow-sm"
          />
          {block.caption && (
            <figcaption className="mt-2.5 text-center text-xs text-zinc-500">{block.caption}</figcaption>
          )}
        </figure>
      );
    case "table":
      return (
        <div className="mt-7 overflow-x-auto rounded-2xl border border-zinc-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-900 text-left">
                {block.headers.map((h) => (
                  <th key={h} className="px-5 py-3.5 font-display font-bold text-white text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAF8F5]"}>
                  {row.map((cell, j) => (
                    <td key={j} className={`px-5 py-3.5 ${j === 0 ? "font-semibold text-zinc-900" : "text-zinc-600"}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return null;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const others = POSTS.filter((p) => p.slug !== slug);
  const offset = POSTS.findIndex((p) => p.slug === slug) + 1;
  const related = Array.from({ length: Math.min(3, others.length) }, (_, i) => others[(offset + i) % others.length]);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: post.title,
        description: post.description,
        datePublished: post.date,
        author: { "@type": "Organization", name: "HoneyGain Deals" },
        publisher: { "@type": "Organization", name: "HoneyGain Deals" },
        mainEntityOfPage: { "@type": "WebPage", "@id": `${window.location.origin}/blog/${post.slug}` },
        keywords: post.keyword,
      },
      {
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] honey-grain font-body text-zinc-900">
      <SeoHead title={`${post.title} | HoneyGain Deals`} description={post.description} path={`/blog/${post.slug}`} jsonLd={jsonLd} image={post.image} />

      <header className="border-b border-amber-100 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-lg text-zinc-900" data-testid="post-logo">
            <span className="grid place-items-center w-8 h-8 rounded-xl bg-amber-400 text-zinc-900">
              <Hexagon className="w-5 h-5" strokeWidth={2.5} />
            </span>
            HoneyGain<span className="text-amber-600">Deals</span>
          </Link>
          <AffiliateCTA label="Get $3 Bonus" testid="post-header-cta" dark />
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-5 sm:px-8 py-12 sm:py-16" data-testid="blog-article">
        <nav className="flex items-center gap-1.5 text-xs font-semibold text-zinc-500" data-testid="post-breadcrumb">
          <Link to="/" className="hover:text-amber-700 transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-amber-700 transition-colors">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-amber-700">Passive income</span>
        </nav>

        <Reveal>
          <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-zinc-900 leading-[1.12]">
            {post.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium text-zinc-500">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {post.date}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readTime}</span>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <img
            src={post.image}
            alt={post.title}
            data-testid="post-hero-image"
            className="mt-8 w-full rounded-3xl border border-zinc-100 shadow-lg shadow-amber-900/5"
          />
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 rounded-3xl border border-amber-200 bg-white p-7 shadow-sm" data-testid="post-tldr">
            <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-700">
              <Zap className="w-4 h-4" /> The short answer
            </p>
            <p className="mt-3 text-base sm:text-lg font-medium text-zinc-800 leading-relaxed">{post.tldr}</p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-4">
            {post.blocks.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <section className="mt-14" data-testid="post-faq">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-900">
              Frequently asked questions
            </h2>
            <Accordion type="single" collapsible className="mt-6 space-y-3">
              {post.faq.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid="post-faq-item"
                  className="rounded-2xl border border-zinc-100 bg-white px-6 data-[state=open]:border-amber-300 data-[state=open]:bg-amber-50/60 transition-colors"
                >
                  <AccordionTrigger className="text-left font-display font-bold text-zinc-900 hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-600 leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 rounded-[2rem] bg-gradient-to-br from-amber-400 via-amber-400 to-amber-500 px-8 py-12 text-center" data-testid="post-final-cta">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-zinc-900">
              Start earning daily — tonight
            </h2>
            <p className="mt-3 text-zinc-800/80 max-w-md mx-auto">
              Join Honeygain through our link and your $3 welcome bonus lands instantly. Free forever,
              two minutes to set up.
            </p>
            <div className="mt-7">
              <AffiliateCTA label="Join Honeygain — Get $3 Free" testid="post-bottom-cta" dark />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <section className="mt-14" data-testid="related-posts">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-zinc-900">
              Keep reading
            </h2>
            <div className="mt-7 grid gap-5 md:grid-cols-3">
              {related.map((r, i) => (
                <Reveal key={r.slug} delay={i * 0.08}>
                  <Link
                    to={`/blog/${r.slug}`}
                    data-testid={`related-post-${r.slug}`}
                    className="group block h-full rounded-3xl border border-zinc-100 bg-white overflow-hidden shadow-sm hover:shadow-xl hover:shadow-amber-900/5 hover:-translate-y-1 transition-all"
                  >
                    <img
                      src={r.image}
                      alt={r.title}
                      loading="lazy"
                      className="w-full aspect-[16/9] object-cover border-b border-zinc-100"
                    />
                    <div className="p-5">
                      <p className="text-[11px] font-semibold text-zinc-500 inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> {r.readTime}
                      </p>
                      <h3 className="mt-2 font-display text-base font-bold text-zinc-900 leading-snug group-hover:text-amber-700 transition-colors">
                        {r.title}
                      </h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </section>
        </Reveal>

        <p className="mt-10 text-xs text-zinc-400 leading-relaxed" data-testid="post-disclosure">
          Disclosure: this article contains an affiliate link. If you sign up through it, we earn a
          commission at no extra cost to you — and you get a $3 welcome bonus. Earnings figures are
          realistic estimates, not guarantees; actual results vary by location and effort.
        </p>
      </article>
    </div>
  );
};

export default BlogPost;
